import React, { useCallback, useEffect, useState } from "react";
import {
  FiEdit3, FiCheckCircle, FiMessageSquare, FiLock, FiAlertTriangle,
} from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  SITUACAO_LABEL,
  finalizarRevisaoRequisicao,
  iniciarRevisaoRequisicao,
  listarRequisicoesDaLista,
  registrarObservacaoRequisicao,
} from "../../services/separacaoFerragens";

/**
 * Painel do SOLICITANTE sobre a requisicao que ele enviou ao estoque.
 *
 * Antes desta entrega o revisor enviava a lista e perdia o fio: nao via o
 * status, nao conseguia corrigir e nao tinha canal para avisar o estoque de
 * nada. Agora ele tem os dois caminhos que o dono do produto pediu:
 *
 *  - REVISAR: enquanto o almoxarife nao concluiu, ele edita a lista e reenvia.
 *    Enquanto a revisao esta aberta o estoque fica BLOQUEADO — por isso a
 *    interface insiste em "Finalizar revisao": esquecer aberta trava o outro lado.
 *  - REGISTRAR OBSERVACAO: vale sempre, inclusive depois de separada. E o que
 *    sobra quando a lista ja foi atendida e nao da mais para mexer nela.
 *
 * Nao renderiza nada quando a lista nunca foi enviada.
 */
export default function PainelRequisicao({ listaId, onRequisicaoMudou }) {
  const { request } = useAuth();
  const toast = useToast();
  const [req, setReq] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [obsAberta, setObsAberta] = useState(false);
  const [texto, setTexto] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    if (!listaId) return;
    try {
      const r = await listarRequisicoesDaLista(request, listaId);
      const itens = Array.isArray(r) ? r : r?.results || [];
      // A mais recente: uma lista pode ter sido enviada, cancelada e reenviada.
      setReq(itens[0] || null);
      setFalhou(false);
    } catch (err) {
      setReq(null);
      // 403/404 sao respostas legitimas: quem nao pode ver requisicao, ou lista
      // que nunca foi enviada, simplesmente nao ve o painel. Qualquer OUTRO
      // erro precisa aparecer — engolir tudo em silencio foi o que fez uma
      // falha de permissao passar por "a funcionalidade nao foi entregue".
      const st = err?.status;
      setFalhou(st !== 403 && st !== 404);
    } finally {
      setCarregando(false);
    }
  }, [request, listaId]);

  useEffect(() => { carregar(); }, [carregar]);

  async function agir(fn, mensagemOk) {
    setOcupado(true);
    try {
      await fn();
      toast.success(mensagemOk);
      await carregar();
      if (onRequisicaoMudou) onRequisicaoMudou();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err.message || "Não foi possível concluir a ação.");
    } finally {
      setOcupado(false);
    }
  }

  async function enviarObservacao() {
    const t = texto.trim();
    if (!t) return;
    await agir(
      () => registrarObservacaoRequisicao(request, req.id, t),
      "Observação registrada. O estoque vai ver na requisição.",
    );
    setTexto("");
    setObsAberta(false);
  }

  if (carregando) return null;

  // Falha inesperada (nao 403/404): avisa em vez de sumir. Painel invisivel por
  // erro e indistinguivel de "esta lista nunca foi enviada ao estoque".
  if (falhou) {
    return (
      <div
        role="status"
        style={{
          border: "1px solid var(--warning-border)", borderRadius: 10,
          padding: "10px 14px", marginBottom: 16, fontSize: 13,
          background: "var(--warning-bg)", color: "var(--warning-text)",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}
      >
        <FiAlertTriangle size={15} />
        <span>Não foi possível carregar a situação desta lista no estoque.</span>
        <button type="button" className="btn btn--secondary" onClick={carregar}>
          Tentar de novo
        </button>
      </div>
    );
  }

  if (!req) return null;

  const emRevisao = !!req.em_revisao;
  // O backend manda `pode_revisar`; a regra nao e reimplementada aqui para as
  // duas pontas nao divergirem.
  const podeRevisar = !!req.pode_revisar;
  const jaSeparada = req.situacao === "SEPARADA";

  return (
    <div
      style={{
        border: "1px solid var(--border)", borderRadius: 10,
        padding: "12px 14px", marginBottom: 16,
        background: emRevisao ? "var(--warning-bg)" : "var(--surface-alt, var(--surface))",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14 }}>Requisição {req.numero}</b>
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {SITUACAO_LABEL[req.situacao] || req.situacao}
        </span>
        {emRevisao ? (
          <span style={{ fontSize: 13, color: "var(--warning-text)", display: "flex", gap: 4, alignItems: "center" }}>
            <FiLock size={13} /> em revisão — estoque bloqueado
          </span>
        ) : null}
      </div>

      {jaSeparada ? (
        <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--ink-3)" }}>
          O estoque já separou esta lista — não é mais possível revisar. Use a
          observação para deixar um recado.
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {emRevisao ? (
          <button
            type="button"
            className="btn btn--primary"
            disabled={ocupado}
            onClick={() => agir(
              () => finalizarRevisaoRequisicao(request, req.id),
              "Revisão finalizada. O estoque já pode retomar a separação.",
            )}
          >
            <FiCheckCircle size={14} /> Finalizar revisão
          </button>
        ) : null}

        {!emRevisao && podeRevisar ? (
          <button
            type="button"
            className="btn btn--secondary"
            disabled={ocupado}
            title="Bloqueia o estoque enquanto você corrige a lista"
            onClick={() => agir(
              () => iniciarRevisaoRequisicao(request, req.id),
              "Revisão iniciada. O estoque ficará bloqueado até você finalizar.",
            )}
          >
            <FiEdit3 size={14} /> Revisar
          </button>
        ) : null}

        <button
          type="button"
          className="btn btn--secondary"
          disabled={ocupado}
          onClick={() => setObsAberta((v) => !v)}
        >
          <FiMessageSquare size={14} /> Registrar observação
        </button>
      </div>

      {obsAberta ? (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <textarea
            className="input"
            rows={3}
            maxLength={2000}
            placeholder="Ex.: o puxador do módulo 3 mudou de modelo."
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => { setObsAberta(false); setTexto(""); }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={ocupado || !texto.trim()}
              onClick={enviarObservacao}
            >
              Enviar ao estoque
            </button>
          </div>
        </div>
      ) : null}

      {req.observacao ? (
        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <b style={{ color: "var(--ink-3)" }}>Observações</b>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 2 }}>{req.observacao}</div>
        </div>
      ) : null}
    </div>
  );
}
